"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";

/**
 * Big square image preview rendered for image-only chat messages.
 * Same broken-state behaviour as ChatImageAttachment — when the
 * <img> fails we drop the broken-icon glyph entirely and show a
 * centered "Image unavailable" placeholder inside the same blue
 * bubble so the conversation layout doesn't shift.
 */
export function ChatImagePreview(t0) {
  const $ = _c(9);
  const {
    image,
    onClick,
    className: t1
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const [broken, setBroken] = useState(!image);
  if (broken) {
    const t2 = `${className} w-[150px] h-[150px] sm:w-[200px] sm:h-[200px] flex flex-col items-center justify-center gap-2 text-white/85 select-none`;
    let t3;
    let t4;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="opacity-75"><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><line x1="3" y1="3" x2="21" y2="21" /></svg>;
      t4 = <span className="text-[12px] font-medium font-poppins italic opacity-90">Image unavailable</span>;
      $[0] = t3;
      $[1] = t4;
    } else {
      t3 = $[0];
      t4 = $[1];
    }
    let t5;
    if ($[2] !== t2) {
      t5 = <div className={t2}>{t3}{t4}</div>;
      $[2] = t2;
      $[3] = t5;
    } else {
      t5 = $[3];
    }
    return t5;
  }
  const t2 = `${className} cursor-pointer w-[150px] sm:w-[200px] h-auto object-cover rounded-md`;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setBroken(true);
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== image || $[6] !== onClick || $[7] !== t2) {
    t4 = <img className={t2} src={image} alt="image" onClick={onClick} onError={t3} />;
    $[5] = image;
    $[6] = onClick;
    $[7] = t2;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}

/**
 * Image attachment pill rendered inside chat-message bubbles.
 *
 * Default state mirrors the inline pill that existed in
 * SendMessage / MessagesInfo / RepsChat: a small thumbnail + the
 * filename + a download button on the right.
 *
 * When the underlying <img> fails to load (typically because the
 * S3 object behind an old AWS URL has been deleted), the entire
 * pill swaps to a calm "Image attachment unavailable" state with
 * no thumbnail, no download button, and italic muted text — so the
 * conversation reads as "the message had an attachment we can't
 * show anymore" instead of broadcasting a broken-image icon.
 */
export default function ChatImageAttachment(t0) {
  const $ = _c(22);
  const {
    image,
    fileName,
    isOwn: t1,
    onPreview,
    onDownload
  } = t0;
  const isOwn = t1 === undefined ? true : t1;
  const [broken, setBroken] = useState(!image);
  const bgClass = isOwn ? "bg-[#354EF0]" : "bg-[#EEEEEE]";
  const textClass = isOwn ? "text-white" : "text-black";
  if (broken) {
    const t2 = `${bgClass} ${textClass} opacity-80 flex items-center gap-2 rounded-lg px-3 py-2 cursor-default select-none`;
    let t3;
    let t4;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 opacity-80"><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><line x1="3" y1="3" x2="21" y2="21" /></svg>;
      t4 = <span className="text-[13px] font-medium font-poppins italic opacity-95">Image attachment unavailable</span>;
      $[0] = t3;
      $[1] = t4;
    } else {
      t3 = $[0];
      t4 = $[1];
    }
    let t5;
    if ($[2] !== t2) {
      t5 = <div className={t2}>{t3}{t4}</div>;
      $[2] = t2;
      $[3] = t5;
    } else {
      t5 = $[3];
    }
    return t5;
  }
  const t2 = `${bgClass} flex items-center gap-10 cursor-pointer justify-between rounded-lg p-2`;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setBroken(true);
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== image) {
    t4 = <img className="cursor-pointer w-full h-full rounded-xl object-cover" src={image} width={20} height={20} alt="image" onError={t3} />;
    $[5] = image;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== onPreview || $[8] !== t4) {
    t5 = <div onClick={onPreview} className="w-[40px] h-[40px] rounded-xl">{t4}</div>;
    $[7] = onPreview;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = `${textClass} text-[14px] font-medium font-poppins`;
  const t7 = fileName || "Image";
  let t8;
  if ($[10] !== t6 || $[11] !== t7) {
    t8 = <div className={t6}>{t7}</div>;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t5 || $[14] !== t8) {
    t9 = <div className="flex items-center gap-2">{t5}{t8}</div>;
    $[13] = t5;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== onDownload) {
    t10 = onDownload && <div onClick={onDownload} className="bg-[#1430E9] p-1.5 rounded-md"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M17.5003 15.8335H2.50033C2.27931 15.8335 2.06735 15.9213 1.91107 16.0776C1.75479 16.2339 1.66699 16.4458 1.66699 16.6668C1.66699 16.8878 1.75479 17.0998 1.91107 17.2561C2.06735 17.4124 2.27931 17.5002 2.50033 17.5002H17.5003C17.7213 17.5002 17.9333 17.4124 18.0896 17.2561C18.2459 17.0998 18.3337 16.8878 18.3337 16.6668C18.3337 16.4458 18.2459 16.2339 18.0896 16.0776C17.9333 15.9213 17.7213 15.8335 17.5003 15.8335Z" fill="white" /><path d="M10.0005 1.6665C9.77947 1.6665 9.5675 1.7543 9.41122 1.91058C9.25494 2.06686 9.16715 2.27882 9.16715 2.49984V11.3248L6.42548 8.57484C6.26856 8.41792 6.05573 8.32976 5.83381 8.32976C5.6119 8.32976 5.39907 8.41792 5.24215 8.57484C5.08523 8.73176 4.99707 8.94459 4.99707 9.1665C4.99707 9.38842 5.08523 9.60125 5.24215 9.75817L9.40881 13.9248C9.48628 14.0029 9.57845 14.0649 9.68 14.1072C9.78155 14.1496 9.89047 14.1713 10.0005 14.1713C10.1105 14.1713 10.2194 14.1496 10.321 14.1072C10.4225 14.0649 10.5147 14.0029 10.5921 13.9248L14.7588 9.75817C14.8365 9.68047 14.8981 9.58823 14.9402 9.48671C14.9822 9.38519 15.0039 9.27639 15.0039 9.1665C15.0039 9.05662 14.9822 8.94781 14.9402 8.8463C14.8981 8.74478 14.8365 8.65254 14.7588 8.57484C14.6811 8.49714 14.5889 8.4355 14.4874 8.39345C14.3858 8.3514 14.277 8.32976 14.1671 8.32976C14.0573 8.32976 13.9485 8.3514 13.8469 8.39345C13.7454 8.4355 13.6532 8.49714 13.5755 8.57484L10.8338 11.3248V2.49984C10.8338 2.27882 10.746 2.06686 10.5897 1.91058C10.4335 1.7543 10.2215 1.6665 10.0005 1.6665Z" fill="white" /></svg></div>;
    $[16] = onDownload;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t2 || $[20] !== t9) {
    t11 = <div className={t2}>{t9}{t10}</div>;
    $[18] = t10;
    $[19] = t2;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  return t11;
}